import React, { useState, useEffect, useMemo } from 'react';
import { useTable, useSortBy } from 'react-table';
import './advance.css';

const AddAdvanceAmount = () => {
  const [selectedFarmer, setSelectedFarmer] = useState('');
  const [advanceDetails, setAdvanceDetails] = useState({
    amount: '',
    date: '',
    mode: '',
    customMode: '',
    description: '',
    customDescription: ''
  });
  const [farmersList, setFarmersList] = useState([]);
  const [advances, setAdvances] = useState([]);
  const [collections, setCollections] = useState([]);
  const [filterMonth, setFilterMonth] = useState('');
  const [filterFarmer, setFilterFarmer] = useState('');
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    fetchFarmers();
    fetchAdvances();
    fetchCollections();
  }, []);

  const fetchFarmers = async () => {
    try {
      const res = await fetch('https://kmtea.onrender.com/farmers');
      const data = await res.json();
      setFarmersList(data);
    } catch (err) {
      console.error('Error fetching farmers:', err);
    }
  };

  const fetchAdvances = async () => {
    try {
      const res = await fetch('https://kmtea.onrender.com/advances');
      const data = await res.json();
      setAdvances(data);
    } catch (err) {
      console.error('Error fetching advances:', err);
    }
  };

  const fetchCollections = async () => {
    try {
      const res = await fetch('https://kmtea.onrender.com/collecttea');
      const data = await res.json();
      setCollections(data);
    } catch (err) {
      console.error('Error fetching collections:', err);
    }
  };

  const handleSave = async () => {
    const { amount, date, mode, customMode, description, customDescription } = advanceDetails;
    if (!selectedFarmer || !amount || !date) {
      alert('Please fill all required fields');
      return;
    }

    const finalMode = mode === 'Others' ? customMode : mode;
    const finalDescription = description === 'Others' ? customDescription : description;

    try {
      const res = await fetch('https://kmtea.onrender.com/add-advance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          farmid: selectedFarmer,
          amount,
          date,
          mode: finalMode,
          description: finalDescription
        })
      });

      if (res.ok) {
        alert('Advance saved');
        setAdvanceDetails({
          amount: '',
          date: '',
          mode: '',
          customMode: '',
          description: '',
          customDescription: ''
        });
        setSelectedFarmer('');
        fetchAdvances();
      } else {
        const error = await res.text();
        alert('Error: ' + error);
      }
    } catch (err) {
      console.error(err);
      alert('Connection error');
    }
  };

  const handleShowDetails = () => {
    setShowDetails(true);
  };

  const { tableData, totalAmount } = useMemo(() => {
    const filtered = advances.filter((a) => {
      const matchFarmer = filterFarmer ? a.farmid === filterFarmer : true;
      const matchMonth = filterMonth
        ? new Date(a.date).toISOString().slice(0, 7) === filterMonth
        : true;
      return matchFarmer && matchMonth;
    });

    const collectionMap = {};
    collections.forEach((c) => {
      const cMonth = new Date(c.date).toISOString().slice(0, 7);
      if (cMonth === filterMonth) {
        collectionMap[c.farmid] = (collectionMap[c.farmid] || 0) + c.qty;
      }
    });

    const result = filtered.map((a) => ({
      ...a,
      totalQty: collectionMap[a.farmid] || 0,
      farmerName: farmersList.find((f) => f.farmid === a.farmid)?.name || a.farmid,
    }));

    return {
      tableData: result,
      totalAmount: result.reduce((sum, a) => sum + parseFloat(a.amount || 0), 0),
    };
  }, [advances, collections, filterFarmer, filterMonth, farmersList]);

  const columns = useMemo(() => {
    const base = [
      { Header: 'Farmer Name', accessor: 'farmerName' },
      {
  Header: 'Date',
  accessor: 'date',
  Cell: ({ value }) => {
    const date = new Date(value);
    return date.toLocaleDateString('en-GB').replace(/\/20/, '/'); // => dd/mm/yy
  }
},
      { Header: 'Amount', accessor: 'amount' },
      { Header: 'Mode of pay', accessor: 'mode' },
      { Header: 'Description', accessor: 'description' },


    ];

    if (filterMonth) {
      base.push({
        Header: 'Total Qty',
        accessor: 'totalQty',
        Cell: ({ row, value }) => {
          if (filterFarmer) {
            return row.index === 0 ? (
              <div className="highlight">{value}</div>
            ) : null;
          }
          return <div>{value}</div>;
        },
      });
    }

    return base;
  }, [filterMonth, filterFarmer]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable({ columns, data: tableData }, useSortBy);

  return (
    <div className="add-advance-container">
      <div className="aad">
        <h3>Advance Details</h3>
        <div className="form-row">
          <select className="advance-month" value={filterFarmer} onChange={(e) => setFilterFarmer(e.target.value)}>
            <option value="">All Farmers</option>
            {farmersList.map((f) => (
              <option key={f.farmid} value={f.farmid}>{f.name} ({f.farmid})</option>
            ))}
          </select>

          <label>Month</label>
          <input
            className="advance-month"
            type="month"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
          />
          <button className="app-button" onClick={handleShowDetails}>Show</button>
        </div>

        {showDetails && (
          <table {...getTableProps()} className="styled-table">
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                      {column.render('Header')}
                      <span>{column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}</span>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.length > 0 ? rows.map((row) => {
                prepareRow(row);
                return (
                  <tr {...row.getRowProps()}>
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                    ))}
                  </tr>
                );
              }) : (
                <tr>
                  <td colSpan={columns.length}>No data found</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={columns.length} style={{ textAlign: 'right', fontWeight: 'bold' }}>
                  Total Amount: ₹{totalAmount.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      <div className="aaa">
        <h2>Add Advance Amount</h2>
        <div className="form-row">
          <label>Farmer</label>
          <select value={selectedFarmer} onChange={(e) => setSelectedFarmer(e.target.value)}>
            <option value="">Select Farmer</option>
            {farmersList.map((f) => (
              <option key={f.farmid} value={f.farmid}>{f.name} ({f.farmid})</option>
            ))}
          </select>

          <label>Amount</label>
          <input
            type="number"
            value={advanceDetails.amount}
            onChange={(e) => setAdvanceDetails({ ...advanceDetails, amount: e.target.value })}
          />

          <label>Date</label>
          <input
            type="date"
            value={advanceDetails.date}
            onChange={(e) => setAdvanceDetails({ ...advanceDetails, date: e.target.value })}
          />

          <label>Mode of Payment</label>
          <select
            value={advanceDetails.mode}
            onChange={(e) => setAdvanceDetails({ ...advanceDetails, mode: e.target.value })}
          >
            <option value="">Select Mode</option>
            <option value="Cash">Cash</option>
            <option value="Online">Online</option>
            <option value="Others">Others</option>
          </select>

          {advanceDetails.mode === 'Others' && (
            <>
              <label>Other Mode</label>
              <input
                type="text"
                value={advanceDetails.customMode}
                onChange={(e) => setAdvanceDetails({ ...advanceDetails, customMode: e.target.value })}
              />
            </>
          )}

          <label>Description</label>
          <select
            value={advanceDetails.description}
            onChange={(e) => setAdvanceDetails({ ...advanceDetails, description: e.target.value })}
          >
            <option value="">Select Description</option>
            <option value="TeaCollection">Tea Collection</option>
            <option value="Others">Others</option>
          </select>

          {advanceDetails.description === 'Others' && (
            <>
              <label>Other Description</label>
              <input
                type="text"
                value={advanceDetails.customDescription}
                onChange={(e) => setAdvanceDetails({ ...advanceDetails, customDescription: e.target.value })}
              />
            </>
          )}

          <button className="app-button" onClick={handleSave}>Save Advance</button>
        </div>
      </div>
    </div>
  );
};

export default AddAdvanceAmount;
